import { FilterTask } from './filter-task';
import { PaginateTask } from './paginate-task';
import { ResetPaginationTask } from './reset-pagination-task';
import { SortTask } from './sort-task';
import { SortConfiguration, SortDirection } from './sort-configuration';
import { GridWidgetTask } from './grid-widget-task';
import { GridWidgetDataField } from './grid-widget-data-field';
import { LinkedList } from '../../utils/grid-widget/linked-list';
import { DataTask } from './data-task';
import { CompleteTask } from './complete-task';
import { FilterContainer } from './filter-container';
import { Subject, forkJoin, of } from 'rxjs';
import { Observable } from 'rxjs';
import { RestArgument } from './rest-argument';
import { OrderedMap } from '../ordered-map';
import {gridWidgetLogger} from '../../utils/grid-widget/grid-widget-logger';
import { MultiField } from './multi-field';
import { fileCreator } from '../../utils/file-creator';
import { pdsI18n } from '../../pds-i18n';
import { pdsLocalStorage } from '../../pds-local-storage';

// pds.logger.requiresLogger('gw');
export enum FilterConstraint {
  BOTH,
  NO_FILTERS,
  SIMPLE_ONLY,
  ADVANCED_ONLY
}

export class GridWidget {
  /**
   * This is the data that gets sorted, filtered, and paginated.
   */
  inputData: Array<any>;

  //Map<Name,DataField>
  dataFields: OrderedMap<GridWidgetDataField> = new OrderedMap<GridWidgetDataField>();

  //Sort order set to -1 so it will be first by default.
  simpleFilterDataField: MultiField = new MultiField('all-fields-filter', pdsI18n.getText('pds|grid widget|All'), this.dataFields, -1);

  /* First task in the chain. Always. */
  dataTask: DataTask = new DataTask(() => {
    return this.inputData;
  });

  //This is the thing to observe if you want to know when the grid widget task chain is complete.
  //This is type Observable because that's what we want the outside world to see.
  //However, the CompleteTask needs a Subject so we cast it when passing it in.
  tasksComplete: Observable<Array<any>> = new Subject<Array<any>>();

  //An observable that fires when the init function completes.
  //This is here so the outside world can know when init completes.
  initComplete: Observable<void> = new Subject<void>();

  sortControl: SortTask = new SortTask(this.dataFields);

  paginationControl: PaginateTask = new PaginateTask();

  resetPaginationTask: ResetPaginationTask = new ResetPaginationTask(this.paginationControl);

  filterControl: FilterTask = new FilterTask(this.dataFields);

  taskChainComplete: CompleteTask;
  /* End Task List*/

  taskList: LinkedList<GridWidgetTask> = new LinkedList<GridWidgetTask>(this.dataTask);

  //'no-filters', 'simple-only', 'advanced-only' are the options
  //This dictates what shows up in the UI.  'advanced-only' is SOP for server side mode.
  //NOTE: This is a set it and forget it configuration type. If you change it dynamically you are expected to call
  //this.init() OR you can update the internal gridWidget configuration properties manually.
  filterConstraint: 'no-filters' | 'simple-only' | 'advanced-only' | null = null;

  //This variable is here for the pds-page-controls to toggle the filters on and off without effecting
  //anything else in the GridWidget configuration object. There was a time when we were just using the
  //filterConstraint property to do this, but there were bugs due to other configurations being set during
  //init based on filterConstraint.
  hideFilterControls = false;

  logColor: string;

  //If any of the observables fail, then the task chain is not started.
  //Using a set here to keep the same function getting added twice.
  taskChainPrerequisites: Set<() => Observable<any>> = new Set<() => Observable<any>>();

  /**
   * The id used to persist the state of this grid widget to local storage.
   */
  statePersistenceId: string;

  /**
   * Constructs a grid widget.
   * @param statePersistenceId The id used to persist the state of this grid widget to local storage or false to disable state persistence.
   * @param logColor The color of log statements originating from this grid widget.
   */
  constructor(
    statePersistenceId: string | false,
    logColor?: string
  ) {
    this.taskChainComplete = new CompleteTask(<Subject<Array<any>>>this.tasksComplete, () => {
      this.tasksCompleteCallback();
    });
    this.logColor = logColor != null ? logColor : 'black';
    this.taskList.addAfter(this.filterControl, this.taskList.head);
    this.taskList.addAfter(this.resetPaginationTask, this.filterControl);
    this.taskList.addAfter(this.sortControl, this.resetPaginationTask);
    this.taskList.addAfter(this.paginationControl, this.sortControl);
    this.taskList.addAfter(this.taskChainComplete, this.paginationControl);

    if (statePersistenceId !== false) {
      if (statePersistenceId == null
        || typeof statePersistenceId !== 'string'
        || statePersistenceId.replace(/\s/g, '').length === 0) {
        throw new Error('statePersistenceId must be a non-empty string or false');
      }
      this.statePersistenceId = 'gw:' + statePersistenceId;
    }
  }

  /**
   * The initialization function. This should be available to be run after the fact.
   * When inputData changes, init needs to be called again.
   */
  init(dataFields: Array<GridWidgetDataField> | { [key: string]: GridWidgetDataField },
    inputData: Array<any> = [],
    sortConfigurations?: Array<SortConfiguration>,
    executeTaskChain = true): void {
    gridWidgetLogger.log('grid widget init', this.logColor);

    this.inputData = inputData;

    this.sortControl.sortConfigurations = sortConfigurations;

    //We remove all previous data fields.
    //If everyone hates this, we can re-evaluate how we want it to work.
    this.dataFields.clear();

    //First add the simpleFilterDataField
    if (this.filterConstraint !== 'advanced-only') {
      this.addDataFields([this.simpleFilterDataField]);
      if (this.filterConstraint === 'simple-only' && this.simpleFilterDataField.placeholderText == null) {
        this.simpleFilterDataField.placeholderText = pdsI18n.getText('pds|grid widget|Filter');
      }
    }

    this.addDataFields(dataFields);

    //TODO: clear all tasks during init rather than just the filter task
    if (this.statePersistenceId) {
      //We need to clear here no matter what, so we do.
      //It gets duplicated in restoreState() if there is something to restore but it should be quick.
      this.filterControl.clear();
      //So now either restoreState will add some filters to the filter container
      //OR we add an empty one ourselves.
      this.restoreState();
      if (this.filterControl.filterContainers.length === 0) {
        //Yes, reset clears it again, but it also ensures that the correct number
        //of filters get added.
        this.filterControl.reset();
      }
    } else {
      this.filterControl.reset();
    }

    //show filter controls if filters have been restored
    if (this.hideFilterControls && this.filterControl.isFiltersApplied()) {
      this.hideFilterControls = false;
    }

    this.taskList.forEach((task: GridWidgetTask) => {
      task.logColor = this.logColor;
    });

    if (executeTaskChain) {
      this.executeTaskChain();
    }

    //Let's any outside widget know that this was called.
    (<Subject<void>>this.initComplete).next();
  }

  /**
   * This function gets run during the execute function in the taskChainComplete Task.
   */
  tasksCompleteCallback(): void {
    this.persistState();
  }

  /* * * * * * * * * * * * * * * * * * * Public Config Options * * * * * * * * * * * * * * * * * * * * */
  //NOTE: The GridWidget and PdsGridWidget objects shouldn't be calling these getters and setters. instead it should access the objects directly.

  /**
   * Puts the GridWidget object into serverside mode.
   * NOTE: It is possible to get out of server-side mode but we are not providing a convenience function
   * for it.
   */
  setServerSideMode(): void {
    //ServerSide mode will turn all of the tasks to inactive
    //This is a slick deal because all of the tasks will still run their pre-run setup, but the data will
    //just propagate right through without filtering, sorting, or paginating.  Then when the FilterComplete fires its
    //event, the implementing component can grab the filter, sort, and pagination specs out to make the REST call.

    this.taskList.forEach((task: GridWidgetTask) => {
      task.setGridWidgetTaskActive(false);
    });

    //Turn off quick search.  It's really expensive to implement it server side.
    this.setFilterConstraint(FilterConstraint.ADVANCED_ONLY);
  }

  useAllFiltersMode(): void {
    this.filterControl.isAllFiltersMode = true;
  }

  /**
   * Removes pagination from the grid widget.  (I hope you didn't want it back)
   */
  turnOffPagination(): void {
    this.taskList.removeNode(this.paginationControl);
    delete this.paginationControl;
  }

  /**
   * This controls what filters show up in the UI.
   * 'simple-only' > Only the single search field that searches everything.
   * 'advanced-only' > only the filters that filter 1 field at a time.
   * 'none' > The filters are completely turned off.
   * undefined > both simple and advanced filters are available in the UI.
   * @param constraint
   */
  setFilterConstraint(constraint: FilterConstraint): void {
    switch (constraint) {
      case FilterConstraint.ADVANCED_ONLY:
        this.filterConstraint = 'advanced-only';
        break;
      case FilterConstraint.BOTH:
        this.filterConstraint = null;
        break;
      case FilterConstraint.NO_FILTERS:
        this.filterConstraint = 'no-filters';
        break;
      case FilterConstraint.SIMPLE_ONLY:
        this.filterConstraint = 'simple-only';
        break;
      default:
        throw constraint + ' not supported as a filter constraint.';
    }
  }

  setShowNextPageInput(showNextPageInput: boolean): void {
    this.paginationControl.showNextPageInput = showNextPageInput;
  }

  setShowRowsPerPage(showRowsPerPage: boolean): void {
    this.paginationControl.showRowsPerPage = showRowsPerPage;
  }

  /**
   * Sets the current sort configuration for the grid widget.
   * This is useful for setting the default sort before initializing the grid widget.
   * It is also useful for manually setting the thing to sort on in flight.
   * @param sortConfiguration
   * @param sortDirection
   */
  setSortConfig(sortConfiguration: SortConfiguration | undefined | null, sortDirection?: SortDirection): void {
    if (sortDirection != null && sortConfiguration != null) {
      sortConfiguration.setSortDirection(sortDirection);
    }
    this.sortControl.update(sortConfiguration, this.dataFields);
  }

  addDataFields(dataFields: Array<GridWidgetDataField> | { [key: string]: GridWidgetDataField }): void {
    //addField subroutine
    let addField = (dataField: GridWidgetDataField) => {
      let index = 0;
      this.dataFields.array.find((df: { key: string; value: GridWidgetDataField }, idx: number) => {
        if (dataField.sortKey != null &&
            df.value.sortKey != null &&
            dataField.sortKey < df.value.sortKey) {
          return true;
        }
        index++;
        return false;
      });

      this.dataFields.put(dataField.id, dataField, index);

      //loop over all of the GridWidgetFilterContainers and add the field to the available fields map.
      this.filterControl.filterContainers.forEach((filterContainer) => {
        filterContainer.availableFilterFields[dataField.id] = dataField;
      });

      this.filterControl.updateAvailableFiltersCount();
    };

    if (dataFields instanceof Array) {
      (<Array<GridWidgetDataField>>dataFields).forEach(addField);
    } else {
      // tslint:disable-next-line:forin
      for (let key in dataFields) {
        addField(dataFields[key]);
      }
    }
  }

  removeDataFields(dataFields: Array<GridWidgetDataField>): number {
    let removed = 0;
    dataFields.forEach((dataField: GridWidgetDataField) => {
      //before we actually remove the field, we need to remove it from anything using it.
      //Right now that is the filterContainers.
      let filtersInUse = this.filterControl.filterContainers.filter((filter: FilterContainer) => {
        return filter.dataField === dataField;
      });

      filtersInUse.forEach((filter: FilterContainer) => {
        this.filterControl.filterContainers.splice(this.filterControl.filterContainers.indexOf(filter), 1);
        removed++;
      });

      this.dataFields.remove(dataField.id);
    });

    //If the number of filters went down to zero, we need to add some more.
    if (this.filterControl.filterContainers.length === 0) {
      this.filterControl.addAnotherFilter();
    }

    return removed;
  }

  /**
   * Adds a task to the main task chain.
   * NOTE: The task chain does not allow duplicates.
   * Tasks are inserted in priority order.  Priority must not change while the task instance is in the task chain.
   * @param gwTask The task that needs to be executed when the task chain is running.
   */
  addTask(gwTask: GridWidgetTask): void {
    if (this.taskList.contains(gwTask)) {
      return;
    }

    let node = this.taskList.head;

    while (node != null && gwTask.getPriority() > node.getPriority()) {
      node = node.nextNode;
    }

    if (node != null) {
      this.taskList.addBefore(gwTask, node);
    }
  }

  removeTask(gwTask: GridWidgetTask): void {
    this.taskList.removeNode(gwTask);
  }

  setRowsPerPage(rowsPerPage: number): void {
    this.paginationControl.rowsPerPage = rowsPerPage;
  }

  /**
   * For some reason TS has trouble comparing functions that return observables across projects so 'any' isn't really
   * an option, it's just there to keep TS happy.
   */
  addTaskChainPrerequisite(prerequisite: () => Observable<any> | any): void {
    this.taskChainPrerequisites.add(<() => Observable<any>>prerequisite);
  }

  // This sets placeholder text on the simple filter field
  setSimpleFilterPlaceholderText(value: string): void {
    this.simpleFilterDataField.setPlaceholderText(value);
  }

  /* * * * * * * * * * * * * * * * * * * End Public Config Options * * * * * * * * * * * * * * * * * * * * */

  /* * * * * * * * * * Doing Server Side Mode? Your accessor functions are here * * * * * * * * * * * */
  setNumberOfRows(rowsInPage: number, totalNumberOfRows: number): void {
    this.paginationControl.setNumberOfRowsInPage(rowsInPage);
    this.paginationControl.setNumberOfRows(totalNumberOfRows);
  }

  /**
   * Each RestArgument is in the form fieldName + operator + value.
   * Example: firstName="brian"
   * Example: page=2
   */
  getRestArguments(): { [key: string]: Array<RestArgument> } {
    let restArgs: { [key: string]: Array<RestArgument> } = {};
    this.taskList.forEach((task: GridWidgetTask) => {
      if (task.restArguments != null) {
        restArgs[task.name] = task.restArguments;
      }
    });
    return restArgs;
  }

  /* * * * * * * * * * * * * * * * * * * * End accessor functions are here * * * * * * * * * * * * * * * * */

  /* * * * * * * * * * * * * * * * * * * * Command Functions * * * * * * * * * * * * * * * * */
  /**
   * Runs the task chain. This gets called from the grid widget directive when filtering, sorting,
   * and changing pages.
   *
   * When nothing is passed in, this method runs the entire task chain. If a task name is passed in,
   * it will start the task chain at the task with that name (Skipping the tasks further up the chain)
   *
   * Finally, it will output the data via event.
   * @param taskName [Optional]
   */
  executeTaskChain(taskName?: string): void {
    let prerequisiteResults: Array<Observable<any>> = [of('at least one success')];
    this.taskChainPrerequisites.forEach((prerequisite: () => Observable<any>) => {
      prerequisiteResults.push(prerequisite());
    });
    forkJoin(...prerequisiteResults).subscribe(() => {
      let task: GridWidgetTask;
      if (taskName == null) {
        //We execute from the second node in the chain since the first node
        //simply provides the data to the rest of the chain.
        task = this.taskList.head.nextNode;
      } else {

        let itr: GridWidgetTask = this.taskList.head;
        while (itr != null && itr.name !== taskName) {
          itr = itr.nextNode;
        }
        task = itr;
        if (itr != null) {
          task = itr;
        }
      }

      if (task != null) {
        gridWidgetLogger.log('- - - - - - Running task chain starting at: ' + task.name + ' - - - - - -', this.logColor);
        task.execute();
      }
    }, () => {
      gridWidgetLogger.log('Not all task chain prerequisites fulfilled', this.logColor);
      //TODO: If we ever decide to notify the outside world that a prerequisite was not met, this is the place to do it.
      let itr: GridWidgetTask = this.taskList.head;
      while (itr != null) {
        itr.taskChainCanceled();
        itr = itr.nextNode;
      }
    });

  }

  clearFilterControls(executeTaskChain = true): void {
    this.filterControl.reset();

    if (executeTaskChain) {
      this.executeTaskChain();
    }
  }

  /**
   * Deletes any state persisted by this grid widget.
   */
  clearPersistedState(): void {
    pdsLocalStorage.removeUserItem(this.statePersistenceId);
  }

  persistState(): void {
    //We need to save off the filters for persisting.
    if (this.statePersistenceId) {
      let persistenceObjects = {};
      this.taskList.forEach((task: GridWidgetTask) => {
        let persistenceObject = task.getPersistenceObject();
        if (persistenceObject != null) {
          persistenceObjects[task.name] = persistenceObject;
        }
      });
      if (Object.keys(persistenceObjects).length > 0) {
        pdsLocalStorage.setUserItem(this.statePersistenceId, JSON.stringify(persistenceObjects));
      } else {
        pdsLocalStorage.removeUserItem(this.statePersistenceId);
      }
    }
  }

  restoreState(): void {
    if (this.statePersistenceId) {
      let persistedJSONString = pdsLocalStorage.getUserItem(this.statePersistenceId);
      if (persistedJSONString != null) {
        try {
          let persistenceObjects = JSON.parse(persistedJSONString);
          this.taskList.forEach((task: GridWidgetTask) => {
            task.restoreFromPersistenceObject(persistenceObjects[task.name]);
          });
        } catch (err) {
          console.error('something went wrong restoring the filters for the grid widget.');
        }
      }
    }
  }

  /* * * * * * * * * * * * * * * * * * * * End Command Functions * * * * * * * * * * * * * * * * */

  /**
   * Create a text file of the specified type. For now, the only file type
   * option is "CSV", but we expect to add more in the future.
   *
   * @param fileType
   * @param dataFields
   * @param objects
   */
  createFile(fileType: 'CSV'): string {
    let objects: Array<any> = this.paginationControl.previousNode.result;
    let dataFields: Array<any> = this.dataFields.values().filter((dataField: GridWidgetDataField) => {
      return !dataField.fieldExportDisabled;
    });

    switch (fileType) {
      case 'CSV':
        return fileCreator.createCSV(objects, dataFields);
      default:
        return fileCreator.createCSV(objects, dataFields);
    }
  }
}
